import { Feedback, TextInput } from 'canoe';
import { Field, FieldAttributes, FieldMetaProps, useFormikContext } from 'formik';
import React, { useCallback } from 'react';

import { OperationOperandAutocompleteField } from './OperationOperandAutocomplete';
import { FieldWithErrors } from './StyledForm';
import { UserPropertyOperandField } from './UserPropertyOperandField';
import { Filter, FilterOperations, FilterTypes } from './types';
import { isUserPropertyFilter } from './utils';

const ComponentSwitcher = React.memo(
  ({ meta, ...props }: { meta: FieldMetaProps<string> } & Record<any, any>) => {
    const { values } = useFormikContext<Filter>();
    const {
      type,
      operation: { type: operationType },
    } = values;

    const getFormattedResults = useCallback(
      (results: (string | number | boolean | Record<string, string | number | boolean>)[]) =>
        results.map((result) => {
          let formattedValue: string | number | boolean;

          if (typeof result === 'object') {
            if (result.name && result.id) {
              return {
                label: result.name,
                value: result.id,
              };
            }
            formattedValue = result.value;
          } else {
            formattedValue = result;
          }

          return {
            label: formattedValue,
            value: formattedValue,
          };
        }) || [],
      [],
    );

    if (type === FilterTypes.UserProperty && isUserPropertyFilter(values)) {
      return (
        <UserPropertyOperandField
          {...props}
          meta={meta}
          getFormattedResults={getFormattedResults}
        />
      );
    }

    const isAutocompleteField =
      operationType === FilterOperations.Equals || operationType === FilterOperations.EqualsNot;

    const isMultiselect =
      operationType === FilterOperations.OneOf || operationType === FilterOperations.NotOneOf;

    const simpleApiQuery = (finder: string) => (search: string) => ({
      query: { finder, q: `${search.trim()}` },
    });

    if (type === FilterTypes.UserId && (isAutocompleteField || isMultiselect)) {
      return (
        <OperationOperandAutocompleteField
          type={FilterTypes.UserId}
          apiQuery={simpleApiQuery('byUserId')}
          getFormattedResults={getFormattedResults}
          isMultiselect={isMultiselect}
          {...props}
        />
      );
    }

    if (type === FilterTypes.UserEmail && (isAutocompleteField || isMultiselect)) {
      return (
        <OperationOperandAutocompleteField
          type={FilterTypes.UserEmail}
          apiQuery={simpleApiQuery('byUserEmail')}
          getFormattedResults={getFormattedResults}
          isMultiselect={isMultiselect}
          {...props}
        />
      );
    }

    if (type === FilterTypes.SegmentId && (isAutocompleteField || isMultiselect)) {
      return (
        <OperationOperandAutocompleteField
          type={FilterTypes.SegmentId}
          getFormattedResults={getFormattedResults}
          isMultiselect={isMultiselect}
          {...props}
        />
      );
    }

    if (type === FilterTypes.ContentId && (isAutocompleteField || isMultiselect)) {
      return (
        <OperationOperandAutocompleteField
          type={FilterTypes.ContentId}
          getFormattedResults={getFormattedResults}
          isMultiselect={isMultiselect}
          {...props}
        />
      );
    }

    return (
      <FieldWithErrors>
        <TextInput
          {...props}
          state={meta.touched && meta.error ? 'danger' : undefined}
          onChange={(e: any) => props.onChange(e.currentTarget.value)}
        />
        {meta.touched && meta.error && (
          <Feedback mt="xxs" color="danger">
            {meta.error}
          </Feedback>
        )}
      </FieldWithErrors>
    );
  },
);

export const OperationOperandInput = React.memo(() => {
  const { setFieldValue } = useFormikContext<Filter>();

  return (
    <Field name="operation.operand">
      {({ field, meta }: FieldAttributes<any>) => (
        <>
          <ComponentSwitcher
            {...field}
            grow
            clearable={false}
            onChange={(value: any) => {
              setFieldValue('operation.operand', value, false);
            }}
            meta={meta}
          />
        </>
      )}
    </Field>
  );
});
